import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import usePageAttachmentTabs from "../Hooks/usePageAttachmentTabs";
import { api_without_cancellation } from "../api/api";
import Sidebar from "../Components/Sidebars/main";
import NavBar from "../Components/Navbars/main";
import ActionBarComponent from "../Components/common/ActionBarComponent";
// import TabsClient from "../Components/new-client-dashboard/Tabs";
import NotesSectionDashboard from "../Components/NotesSectionDashboard/main";
import { fetchPageObject, fetchShakespeareStatus, getCaseId, getClientId } from "../Utils/helper";
import Footer from "../Components/common/footer";
import PhotoTab from "../Components/new-client-dashboard/tabs/photo-tab/photo";
import PageDocsTab, { createDocsTab } from "../Components/DocPage/new-tab/page-docs-tab";
import NewCaseExpertModal from "../Components/Experts/ExpertsModals/NewCaseExpertModal";
import ExpertsSummaryPage from "../Components/Experts/ExpertsSummaryPage";
import ExpertsDetailPage from "../Components/Experts/ExpertsDetailPage";
import DatesIcon from "../assets/images/dates.svg";
import DetailIcon from "../assets/images/detail.svg";
import addIconGold from "../assets/Add-icon-gold.svg";
// import TreatmentTabs from "../Components/TreatmentPage/TreatmentTabs";
// import NewChangesTabs from "../Components/new-client-dashboard/NewChangesTabs";
import SmartResponsiveTabsContainer from "../Components/DefendantPage/DefandantMain/components/ResponsiveTabDefendants";
import PlaceHolderifNothingAdded from "../Components/components-new-final/placeholder-nothing-added";
import "./ExpertsPage.css";

const PageContent = ({
  currentPage,
  experts,
  viewType,
  handleFetchExperts,
  pageName,
  pageIcon,
}) => {
  // Filter experts based on current page
  const getFilteredExperts = () => {
    if (currentPage === "all") {
      return experts;
    }

    if (currentPage?.startsWith("plaintiff_")) {
      const expertId = parseInt(currentPage.replace("plaintiff_", ""));
      return experts?.filter((expert) => expert?.retained_by_record_id === expertId);
    }

    if (currentPage?.startsWith("defendant_")) {
      const expertId = parseInt(currentPage.replace("defendant_", ""));
      return experts?.filter((expert) => expert?.retained_by_record_id === expertId);
    }

    return experts;
  };

  const filteredExperts = getFilteredExperts();

  switch (currentPage) {
    case "docs":
      return (
        <PageDocsTab
          key={`${getClientId()}-${getCaseId()}`}
          pageId={65}
          pageName={pageName}
          pageIcon={pageIcon}
        />
      );
    case "photo":
      return <PhotoTab pageId={65} pageName={"Experts"} />;
    default:
      // Check if we should show summary or detailed view
      if (viewType === "summary") {
        return (
          <ExpertsSummaryPage
            experts={filteredExperts || []}
            handleFetchExperts={handleFetchExperts}
          />
        );
      } else {
        // Detailed view (default)
        return (
          <ExpertsDetailPage
            experts={filteredExperts || []}
            handleFetchExperts={handleFetchExperts}
          />
        );
      }
  }
};

function ExpertsPage() {
  const dispatch = useDispatch();
  // const client = useSelector((state) => state.todos.client);
  // const currentCase = useSelector((state) => state.todos.currentCase);
  const currentCaseId = getCaseId();
  const clientId = getClientId();

  const [currentPage, setCurrentPage] = useState("all");
  const [experts, setExperts] = useState([]);
  const [viewType, setViewType] = useState("summary");
  const isMediaPage = currentPage === "photo" || currentPage === "docs";
  const activeViewType = isMediaPage ? null : viewType;
  const [showAddExpertModal, setShowAddExpertModal] = useState(false);
  const [pageObject, setPageObject] = useState(null);
  const [expertsLength, setExpertsLength] = useState(0); //

  const [hasExperts, setHasExperts] = useState(0);

  useEffect(() => {
    setHasExperts(expertsLength);
  }, [expertsLength]);
  useEffect(() => {
    if (currentCaseId && !pageObject) {
      fetchPageObject(currentCaseId, 65).then((data) => {
        setPageObject(data);
      });
    }
  }, [currentCaseId, pageObject]);

  const blockedExpert = useMemo(() => {
    return experts?.find((expert) => expert?.is_blocked === true);
  }, [experts]);

  const hasBlockedExpert = useMemo(() => {
    return !!blockedExpert;
  }, [blockedExpert, experts]);

  const handlePageChange = useCallback((pageType) => {
    setCurrentPage(pageType);
  }, []);

  const handleCloseAddModal = () => {
    setShowAddExpertModal(false);
  };

  const fecthExperts = async () => {
    try {
      const response = await api_without_cancellation.get(
        `/api/experts/${clientId}/${currentCaseId}/`
      );
      if (response.status === 200) {
        setExperts(response.data);
        setExpertsLength(response?.data.length);
      }
    } catch (error) {
      console.error("Error fetching experts:", error);
    }
  };

  const handleFetchExperts = () => {
    fecthExperts();
  };

  useEffect(() => {
    fecthExperts();
    // sending page ids directly now.
    fetchShakespeareStatus(currentCaseId, 65, dispatch);
  }, [currentCaseId, dispatch]);

  const hasAnyExperts = useMemo(() => {
    return experts && experts.length > 0;
  }, [experts, experts?.length]);

  // Main tabs: All, Plaintiff, Defendants
  const mainTabs = useMemo(() => {
    const tabs = [];

    tabs.push({
      id: "all",
      label: "All",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-5)",
      activeColor: "white",
      onClick: () => handlePageChange("all"),
      leftHand: false,
    });

    const seen = new Set();
    experts?.forEach((expert) => {
      const entity = expert?.retained_by_entity;
      const recordId = expert?.retained_by_record_id;

      const dedupKey = `${entity}_${recordId}`;

      if (recordId !== null) {
        if (seen.has(dedupKey)) return;
        seen.add(dedupKey);
      }

      const config = {
        Client: { icon: "ic-client", prefix: "plaintiff", useEntityName: false },
        Defendant: { icon: "ic-defendants", prefix: "defendant", useEntityName: true },
        OtherParty: { icon: "ic-parties", prefix: "party", useEntityName: false },
      }[entity];

      if (!config) return;

      const { first_name = "", last_name = "", entity_name = "" } = expert?.entity_obj || {};
      const isEntity = config.useEntityName && (expert?.entity_obj?.defendantType?.id || 1) !== 1;
      const label = isEntity ? entity_name : `${first_name}${last_name ? `, ${last_name}` : ""}`;

      const id = `${config.prefix}_${recordId}`;
      tabs.push({
        id,
        label: (
          <span className="d-flex align-items-center justify-content-center" style={{ gap: "5px" }}>
            <span className="expert-retained-by-prefix">Retained:</span>
            <i className={`ic ic-15 ${config.icon}`} />
            {label}
          </span>
        ),
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        activeColor: "white",
        onClick: () => handlePageChange(id),
        leftHand: false,
      });
    });

    return tabs;
  }, [handlePageChange, hasExperts, experts, hasAnyExperts]);

  // Button tabs: Summary, Detail, Expert (+)
  const viewTabs = useMemo(() => {
    const tabs = [];

    tabs.push(
      {
        id: "summary",
        label: "Summary",
        className:
          activeViewType === "summary"
            ? "active tab-trapezium-cycles-corners"
            : "tab-trapezium-cycles-corners",
        background:
          activeViewType === "summary" ? "var(--primary) !important" : "var(--primary-60)",
        activeColor: "white",
        onClick: () => {
          handlePageChange("all");
          setViewType("summary");
        },
        leftHand: true,
        span: (
          <span
            style={{
              width: "15px",
              height: "15px",
            }}
            className="fw-bold d-flex align-items-center justify-content-center m-r-5 text-gold"
          >
            <img
              src={DatesIcon}
              style={{
                width: "15px",
                height: "15px",
              }}
              alt="Detail"
            />
          </span>
        ),
      },
      {
        id: "detailed",
        label: "Detail",
        className:
          activeViewType === "detailed"
            ? "active tab-trapezium-cycles-corners"
            : "tab-trapezium-cycles-corners",
        background:
          activeViewType === "detailed" ? "var(--primary) !important" : "var(--primary-60)",
        activeColor: "white",
        onClick: () => {
          if (isMediaPage) handlePageChange("all");
          setViewType("detailed");
        },
        leftHand: true,
        span: (
          <span
            style={{
              width: "15px",
              height: "15px",
            }}
            className="fw-bold d-flex align-items-center justify-content-center m-r-5 text-gold"
          >
            <img
              src={DetailIcon}
              style={{
                width: "15px",
                height: "15px",
              }}
              alt="Detail"
            />
          </span>
        ),
      }
    );

    return tabs;
  }, [activeViewType, isMediaPage, handlePageChange]);
  const buttonTabs = useMemo(() => {
    const tabs = [];
    tabs.push({
      id: "expert-add",
      label: "Expert",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-60)",
      left: "0px",
      onClick: () => setShowAddExpertModal(true),
      leftHand: true,
      span: (
        <span className="fw-bold  ic-15 d-flex align-items-center">
          <img className="d-flex align-items-center justify-content-center" src={addIconGold} />
        </span>
      ),
    });

    return tabs;
  }, [hasAnyExperts, viewType]);

  // Photos tab
  const photoTabs = useMemo(
    () => [
      createDocsTab(() => handlePageChange("docs")),
      {
        id: "photo",
        label: "Photos",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-60)",
        activeColor: "white",
        left: "0px",
        onClick: () => handlePageChange("photo"),
        leftHand: true,
        span: (
          <span
            style={{
              width: "15px",
              height: "15px",
            }}
            className="fw-bold d-flex align-items-center justify-content-center text-gold"
          >
            <img
              src={
                "https://simplefirm-bucket.s3.amazonaws.com/static/images/photo-icon-color_2CyRX1a_GgdCtlG.svg"
              }
              style={{
                width: "15px",
                height: "15px",
              }}
              alt="Detail"
            />
          </span>
        ),
      },
    ],
    []
  );
  const attachmentTabs = usePageAttachmentTabs(65, photoTabs);

  const [sideBarCollapsed, setSideBarCollapsed] = useState(null);

  useEffect(() => {
    const getUserPreferencesFromStorage = () => {
      const preferences = localStorage.getItem("userPreferences");
      const preferencesObj = preferences ? JSON.parse(preferences) : null;
      setSideBarCollapsed(preferencesObj?.SidebarView);
    };
    getUserPreferencesFromStorage();
  }, []);

  const adjustReportColumnWidths = () => {
    const pinColumn = (selector) => {
      const cols = document.querySelectorAll(selector);
      if (!cols.length) return;
      let maxWidth = 0;
      cols.forEach((col) => {
        maxWidth = Math.max(maxWidth, col.getBoundingClientRect().width);
      });
      cols.forEach((col) => {
        col.style.width = `${maxWidth}px`;
        col.style.minWidth = `${maxWidth}px`;
        col.style.maxWidth = `${maxWidth}px`;
      });
    };

    pinColumn(".expert-col");
    pinColumn(".retained-col");
    pinColumn(".expertise-col");
    pinColumn(".deposition-col");
  };

  useEffect(() => {
    adjustReportColumnWidths();
    window.addEventListener("resize", adjustReportColumnWidths);
    return () => window.removeEventListener("resize", adjustReportColumnWidths);
  }, [experts, viewType]);
  return (
    <>
      <div className={`page-wrapper ${sideBarCollapsed ? "force-collapsed" : ""}`}>
        <Sidebar />
        <div className="page-container overflow-hidden">
          <NavBar flaggedPageName="65" />
          <ActionBarComponent
            src={pageObject?.page_icon}
            page_name={pageObject?.name}
            pageObj={pageObject}
            isChecklist={pageObject?.PageChklst || false}
            isDummy={hasBlockedExpert ? true : false}
          />
          <div
            className={`TreatmentPage-main-container invisible-scrollbar experts-page ${
              viewType === "detailed" && !isMediaPage ? "experts-detail-view" : ""
            }`}
          >
            <div
              className="w-100"
              style={{
                position: "sticky",
                background: "white",
                top: "0px",
                zIndex: "10",
              }}
            >
              <SmartResponsiveTabsContainer
                isEmpty={!hasAnyExperts || hasBlockedExpert}
                HandlePage={currentPage}
                tabsData={mainTabs}
                viewTab={viewTabs}
                viewTabControlled={true}
                addTab={buttonTabs}
                photoTab={attachmentTabs}
                photoTabControlled={true}
                handlePageChange={handlePageChange}
                viewType={activeViewType}
              />
            </div>
            {!hasBlockedExpert || isMediaPage ? (
              experts?.length === 0 && !isMediaPage ? (
                <PlaceHolderifNothingAdded
                  text1="No Experts added to the Case"
                  text2="Click to Add"
                  pageIcon={pageObject?.page_icon}
                  onClick={() => setShowAddExpertModal(true)}
                />
              ) : (
                <PageContent
                  currentPage={currentPage}
                  experts={experts}
                  viewType={viewType}
                  handleFetchExperts={handleFetchExperts}
                  pageName={pageObject?.name}
                  pageIcon={pageObject?.page_icon}
                />
              )
            ) : null}

            {hasBlockedExpert && !isMediaPage && (
              <>
                <PlaceHolderifNothingAdded
                  text1="No Experts on the Case"
                  text2="Page is Marked Complete"
                  text3={"To Add Experts to the Case Click Here"}
                  pageIcon={pageObject?.page_icon}
                  completed
                  onClick={() => setShowAddExpertModal(true)}
                />
              </>
            )}

            <NotesSectionDashboard pageName={"Experts"} />
          </div>
        </div>
      </div>
      <Footer />
      {showAddExpertModal && (
        <NewCaseExpertModal
          show={showAddExpertModal}
          handleClose={handleCloseAddModal}
          fetchExperts={handleFetchExperts}
          blockedExpert={blockedExpert}
          experts={hasBlockedExpert ? [] : experts}
        />
      )}
    </>
  );
}

export default ExpertsPage;
